import { type Pitch, alterSymbol, mod, STEPS } from './pitch';
import { type KeySig, keyTonic } from './keysig';

export type NoteNameSystem = 'none' | 'abc' | 'german' | 'degree' | 'solfege-fixed' | 'solfege-movable';

const SOLFEGE = ['Do', 'Re', 'Mi', 'Fa', 'Sol', 'La', 'Ti'];

/** Display name of a pitch class in a note-name system (a display setting, not data). */
export function noteLabel(p: Pick<Pitch, 'step' | 'alter'>, system: NoteNameSystem, key?: KeySig): string {
  switch (system) {
    case 'none':
      return '';
    case 'abc':
      return p.step + alterSymbol(p.alter);
    case 'german': {
      if (p.step === 'B') return p.alter === -1 ? 'B' : p.alter === 0 ? 'H' : p.alter === 1 ? 'His' : p.alter === -2 ? 'Heses' : 'Hisis';
      const base = p.step;
      const vowel = base === 'E' || base === 'A';
      if (p.alter === 0) return base;
      if (p.alter > 0) return base + 'is'.repeat(p.alter);
      return base + (vowel ? 's' : 'es') + (p.alter === -2 ? 'es' : '');
    }
    case 'solfege-fixed':
      return SOLFEGE[STEPS.indexOf(p.step)] + alterSymbol(p.alter);
    case 'solfege-movable':
    case 'degree': {
      const k = key ?? { fifths: 0, mode: 'major' as const };
      const tonic = keyTonic({ fifths: k.fifths, mode: 'major' });
      const deg = mod(STEPS.indexOf(p.step) - STEPS.indexOf(tonic.step), 7);
      // Chromatic alteration relative to the major scale on the tonic.
      const MAJOR = [0, 2, 4, 5, 7, 9, 11];
      const SEMI: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
      const pcs = mod(SEMI[p.step] + p.alter - (SEMI[tonic.step] + tonic.alter), 12);
      const diff = pcs - MAJOR[deg];
      const d = diff > 6 ? diff - 12 : diff < -6 ? diff + 12 : diff;
      if (system === 'degree') return (d < 0 ? '♭'.repeat(-d) : '♯'.repeat(d)) + String(deg + 1);
      return SOLFEGE[deg] + alterSymbol(d);
    }
  }
}
